import React, { useEffect, useMemo } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Code,
  Group,
  Modal,
  NumberInput,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { FiPlay } from "react-icons/fi";
import { openapi } from "../../openapi";
import { useAppDispatch } from "../app/hooks";
import { setDrofusForekomsterSelectedRunId } from "./drofusForekomsterSlice";

type SyncFormValues = {
  top: number | "";
  maxRows: number | "";
  dryRun: boolean;
};

type Props = {
  projectNo: number;
  disabled?: boolean;
  runningRunId?: string | null;
  onStarted?: () => void;
  onPendingChange?: (pending: boolean) => void;
};

const numberOrUndefined = (value: number | "") =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

export const DrofusSyncJobButton: React.FC<Props> = ({
  projectNo,
  disabled = false,
  runningRunId,
  onStarted,
  onPendingChange,
}) => {
  const dispatch = useAppDispatch();
  const [opened, { open, close }] = useDisclosure(false);
  const [triggerSync, { isLoading }] =
    openapi.endpoints.postMultiplacerDrofusInterfaceOccurrencesSync.useMutation();

  const form = useForm<SyncFormValues>({
    initialValues: {
      top: 1000,
      maxRows: 10000,
      dryRun: false,
    },
    validate: {
      top: (value) => {
        if (value === "") return null;
        if (value < 1 || value > 5000) return "Må være mellom 1 og 5000";
        return null;
      },
      maxRows: (value) => {
        if (value === "") return null;
        if (value < 1 || value > 100000) return "Må være mellom 1 og 100000";
        return null;
      },
    },
  });

  const requestBody = useMemo(
    () => ({
      projectNo,
      ...(numberOrUndefined(form.values.top) ? { top: numberOrUndefined(form.values.top) } : {}),
      ...(numberOrUndefined(form.values.maxRows)
        ? { maxRows: numberOrUndefined(form.values.maxRows) }
        : {}),
      dryRun: form.values.dryRun,
    }),
    [form.values.dryRun, form.values.maxRows, form.values.top, projectNo]
  );

  const hasRunningRun = Boolean(runningRunId);

  useEffect(() => {
    onPendingChange?.(isLoading);
  }, [isLoading, onPendingChange]);

  const handleSubmit = form.onSubmit(async () => {
    try {
      onStarted?.();
      const result = await triggerSync({ body: requestBody }).unwrap();
      dispatch(setDrofusForekomsterSelectedRunId(result.runId));
      notifications.show({
        color: "green",
        title: "dRofus-jobb startet",
        message: `Forekomstjobben er lagt i kø. Run ID: ${result.runId}`,
      });
      close();
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Kunne ikke starte dRofus-jobb",
        message: error instanceof Error ? error.message : "Se konsollen eller backend-loggene.",
      });
    }
  });

  return (
    <>
      <Button
        size="xs"
        radius="sm"
        leftSection={<FiPlay size={14} />}
        disabled={disabled || hasRunningRun}
        loading={isLoading}
        onClick={open}
      >
        Start jobb
      </Button>

      <Modal opened={opened} onClose={close} title="Start dRofus-forekomstjobb" size="md" centered>
        <form onSubmit={handleSubmit}>
          <Stack gap="sm">
            {hasRunningRun && (
              <Alert color="yellow" variant="light" radius="sm">
                En dRofus-kjøring pågår allerede. Vent til den er ferdig før du starter en ny.
              </Alert>
            )}

            <NumberInput
              label="Page size"
              description="Antall rader per request mot dRofus."
              min={1}
              max={5000}
              allowDecimal={false}
              {...form.getInputProps("top")}
            />

            <NumberInput
              label="Maks rader"
              description="Tomt felt betyr ingen maksgrense."
              min={1}
              max={100000}
              allowDecimal={false}
              {...form.getInputProps("maxRows")}
            />

            <Checkbox
              label="Dry run"
              description="Henter og validerer data, men skriver ikke snapshots til databasen."
              {...form.getInputProps("dryRun", { type: "checkbox" })}
            />

            <Stack gap={4}>
              <Text size="sm" fw={600}>
                Body
              </Text>
              <Code block>{JSON.stringify(requestBody, null, 2)}</Code>
            </Stack>

            <Group justify="flex-end" gap="xs">
              <Button size="xs" variant="subtle" radius="sm" onClick={close}>
                Avbryt
              </Button>
              <Button
                size="xs"
                radius="sm"
                type="submit"
                loading={isLoading}
                disabled={hasRunningRun}
              >
                Start
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
};
